import { blinkConstants } from '@thoughtspot/blink-constants';
import { parseFloatNumber } from '@thoughtspot/number-format-util';
import { convertValueFromBackend, getId, isAttribute, isEffectivelyNumeric, isGrowth, isMeasure, } from '@thoughtspot/visualization-column-util';
import _ from 'lodash';
import { getAxisConfig, getLegendColumns, getXAxisColumns, getYAxisColumns, isSorted, } from '../../core/chart-model-utils';
import { getName } from '../../core/column-name-util';
import { isMeasureValueColumn } from '../../core/measure-names-and-values';
import { getSortOrder } from './dx-table-util';
export var SortBy;
(function (SortBy) {
    SortBy["NONE"] = "none";
    SortBy["VALUE"] = "value";
})(SortBy || (SortBy = {}));
export const DEFAULT_KEY_KEYWORD = 'key';
export const ROW_EXPAND_PATH_KEY = 'rowExpandedPaths';
export const COLUMN_EXPAND_PATH_KEY = 'columnExpandedPaths';
export const FIELDS = 'fields';
export const getDatasetKey = (columns) => {
    return _.reduce(columns, (key, column) => {
        if (!isMeasure(column)) {
            key += `_${getId(column)}`;
        }
        return key;
    }, DEFAULT_KEY_KEYWORD);
};
export const getProcessedSummarySetForPivotQuery = (query, dataset, getDataRowAttribute, summaryData, hasAttributes) => {
    var _a, _b;
    const columnsInDataOrder = query.getColumnsInDataOrder();
    const processedSummarySet = hasAttributes && summaryData ? summaryData.processedSummarySet : {};
    const dataColumns = _.compact([
        ...((_a = query.yAxisColumns) !== null && _a !== void 0 ? _a : []),
        ...((_b = query.measureValueColumns) !== null && _b !== void 0 ? _b : []),
    ]);
    const dataColumnIds = dataColumns.map(yColumn => yColumn.id);
    const dataColumnsMap = [];
    dataColumns.forEach(column => {
        dataColumnsMap[column.id] = column;
    });
    const skipProcessingDataRow = {};
    dataset.forEach((dataRow, dataRowIdx) => {
        const summarySetKey = _.reduce(dataRow, (key, val, idx) => {
            if (columnsInDataOrder[idx] &&
                !_.includes(dataColumnIds, columnsInDataOrder[idx].id)) {
                const realValue = getDataRowAttribute(convertValueFromBackend(columnsInDataOrder[idx], val));
                key += `_${realValue}`;
            }
            return key;
        }, DEFAULT_KEY_KEYWORD);
        if (!_.isUndefined(processedSummarySet[summarySetKey]) &&
            !!skipProcessingDataRow[summarySetKey]) {
            return;
        }
        const summarySetValues = {};
        dataRow.forEach((val, idx) => {
            const column = columnsInDataOrder[idx];
            if (_.includes(dataColumnIds, column === null || column === void 0 ? void 0 : column.id)) {
                if (isMeasureValueColumn(column)) {
                    summarySetValues[getId(column)] = `${dataRowIdx}:${val}`;
                }
                else {
                    summarySetValues[getId(column)] = val;
                }
            }
        });
        if (_.isUndefined(processedSummarySet[summarySetKey])) {
            processedSummarySet[summarySetKey] = summarySetValues;
        }
        else {
            const oldSetValues = processedSummarySet[summarySetKey];
            Object.keys(oldSetValues).forEach(key => {
                const val = summarySetValues[key];
                if (_.isUndefined(val) || val === '') {
                    return;
                }
                const column = dataColumnsMap[key];
                if (hasAttributes && isEffectivelyNumeric(column)) {
                    oldSetValues[key] = val;
                    return;
                }
                if (_.isArray(oldSetValues[key])) {
                    oldSetValues[key].push(val);
                    if (!isAttribute(column)) {
                        skipProcessingDataRow[summarySetKey] = true;
                    }
                }
                else {
                    const tmpValue = oldSetValues[key];
                    oldSetValues[key] = [tmpValue, val];
                    if (!isAttribute(column)) {
                        skipProcessingDataRow[summarySetKey] = true;
                    }
                }
                oldSetValues[key] = _.flatten(oldSetValues[key]);
            });
        }
    });
    return processedSummarySet;
};
export function getKeyValueFromCellValue(cellValue) {
    var _a, _b;
    if (_.isNil(cellValue) ||
        cellValue.toString().split(':').length < 2) {
        return {
            key: undefined,
            value: cellValue
                ? parseFloatNumber(cellValue.toString())
                : undefined,
        };
    }
    const [keyPart, valuePart] = cellValue.toString().split(':');
    const key = (_a = _.parseInt(keyPart)) !== null && _a !== void 0 ? _a : undefined;
    if (valuePart === 'null') {
        return { key, value: null };
    }
    let value = (_b = parseFloatNumber(valuePart)) !== null && _b !== void 0 ? _b : cellValue;
    if (isNaN(value)) {
        value = parseFloat(valuePart !== null && valuePart !== void 0 ? valuePart : '0');
    }
    return { key, value };
}
function getQueriesForRowExpansion(chartModel, expandPathLength, rowExpandedPathLengthByField, maxColExpanded) {
    const y = getYAxisColumns(chartModel);
    const hasOnlyAttributeValues = y.every(yAxis => isAttribute(yAxis));
    if (hasOnlyAttributeValues) {
        return [];
    }
    const yAxisMeasures = y.filter(yAxisColumn => isEffectivelyNumeric(yAxisColumn));
    const x = getXAxisColumns(chartModel);
    const color = getLegendColumns(chartModel);
    const queryDefs = [];
    for (let i = expandPathLength; i <= rowExpandedPathLengthByField; i++) {
        const rowColumns = x.slice(0, i + 1);
        const colColumns = color.slice(0, maxColExpanded + 1);
        queryDefs.push({
            x: rowColumns,
            y: yAxisMeasures,
            color: [],
        });
        colColumns.forEach((col, idx) => {
            queryDefs.push({
                x: rowColumns,
                y: yAxisMeasures,
                color: colColumns.slice(0, idx + 1),
            });
        });
    }
    return queryDefs;
}
function getQueriesForColumnExpansion(chartModel, expandPathLength, columnExpandedPathLengthByField, maxRowExpanded) {
    const y = getYAxisColumns(chartModel);
    const hasOnlyAttributeValues = y.every(yAxis => isAttribute(yAxis));
    if (hasOnlyAttributeValues) {
        return [];
    }
    const yAxisMeasures = y.filter(yAxisColumn => isEffectivelyNumeric(yAxisColumn));
    const x = getXAxisColumns(chartModel);
    const color = getLegendColumns(chartModel);
    const queryDefs = [];
    for (let i = expandPathLength; i <= columnExpandedPathLengthByField; i++) {
        const rowColumns = x.slice(0, maxRowExpanded + 1);
        const colColumns = color.slice(0, i + 1);
        queryDefs.push({
            x: [],
            y: yAxisMeasures,
            color: colColumns,
        });
        rowColumns.forEach((col, idx) => {
            queryDefs.push({
                x: rowColumns.slice(0, idx + 1),
                y: yAxisMeasures,
                color: colColumns,
            });
        });
    }
    return queryDefs;
}
export function getMaxExpandedPathLength(area, expandPathLength, pivotState, axisConfigColumns) {
    const fields = (pivotState === null || pivotState === void 0 ? void 0 : pivotState.fields) || [];
    const areaFields = _.sortBy(fields.filter(field => {
        return field.area === area;
    }), 'areaIndex');
    const expandAreaFields = areaFields.filter(field => field.expanded);
    let expandedPathLengthByField = expandPathLength;
    for (let i = expandPathLength; i < areaFields.length; i++) {
        if (!areaFields[i].expanded) {
            break;
        }
        expandedPathLengthByField += 1;
    }
    if (!expandAreaFields.length)
        return expandedPathLengthByField;
    const leafColumnId = _.last(expandAreaFields).dataField;
    const columnPathLength = axisConfigColumns.findIndex(column => column.id === leafColumnId) + 1;
    return Math.max(columnPathLength, expandedPathLengthByField);
}
export const generateQueriesForPivotExpandOperation = (expandArea, expandPathLength, pivotState, chartModel) => {
    const rowExpandedPaths = _.get(pivotState, [ROW_EXPAND_PATH_KEY], []);
    const columnExpandedPaths = _.get(pivotState, [COLUMN_EXPAND_PATH_KEY], []);
    const maxRowExpanded = _.max(_.map(rowExpandedPaths, expandedPathArray => expandedPathArray.length)) || 0;
    const maxColExpanded = _.max(_.map(columnExpandedPaths, expandedPathArray => expandedPathArray.length)) || 0;
    if (expandArea === blinkConstants.pivotTable.Areas.ROW) {
        const rowExpandedPathLengthByField = getMaxExpandedPathLength(expandArea, expandPathLength, pivotState, getAxisConfig(chartModel).x);
        if (rowExpandedPathLengthByField > maxRowExpanded) {
            return getQueriesForRowExpansion(chartModel, expandPathLength, rowExpandedPathLengthByField, maxColExpanded);
        }
    }
    if (expandArea === blinkConstants.pivotTable.Areas.COLUMN) {
        const colExpandedPathLengthByField = getMaxExpandedPathLength(expandArea, expandPathLength, pivotState, getAxisConfig(chartModel).y);
        if (colExpandedPathLengthByField > maxColExpanded) {
            return getQueriesForColumnExpansion(chartModel, expandPathLength, colExpandedPathLengthByField, maxRowExpanded);
        }
    }
    return [];
};
export function createPivotStateFieldMap(pivotState) {
    const pivotStateFieldMap = new Map();
    pivotState === null || pivotState === void 0 ? void 0 : pivotState.fields.forEach(field => pivotStateFieldMap.set(field.dataField, field));
    return pivotStateFieldMap;
}
export function getSortingFunctionForMeasureColumn(measureColumns) {
    const indexMap = measureColumns.reduce((acc, column, index) => {
        acc[getName(column)] = index;
        return acc;
    }, {});
    return (a, b) => {
        const aValue = indexMap[a.value];
        const bValue = indexMap[b.value];
        if (aValue === bValue) {
            return 0;
        }
        return aValue < bValue ? -1 : 1;
    };
}
export function getSortInfoFromAnswerColumn(_rows, _columns, _measures, chartModel) {
    const sortingMap = new Map();
    let measureSortColumnId = '';
    _rows.forEach(row => {
        if (isSorted(chartModel, row)) {
            const columnId = getId(row);
            sortingMap.set(columnId, {
                sortBySummaryField: columnId,
                sortOrder: getSortOrder(chartModel, row),
            });
        }
    });
    _columns.forEach(column => {
        if (isSorted(chartModel, column)) {
            const columnId = getId(column);
            sortingMap.set(columnId, {
                sortBySummaryField: columnId,
                sortOrder: getSortOrder(chartModel, column),
            });
        }
    });
    _measures.forEach(measure => {
        if (isSorted(chartModel, measure)) {
            const columnId = getId(measure);
            sortingMap.set(columnId, {
                sortBySummaryField: columnId,
                sortOrder: getSortOrder(chartModel, measure),
            });
            measureSortColumnId = columnId;
        }
    });
    return { sortingMap, measureSortColumnId };
}
export function isSortingStateSaved(pivotState) {
    const result = pivotState === null || pivotState === void 0 ? void 0 : pivotState.fields.some(field => field.sortBySummaryField || field.sortBy === SortBy.NONE);
    return !!result;
}
export function hasRowsOrColumnsExpandedManually(pivotState) {
    return ((pivotState === null || pivotState === void 0 ? void 0 : pivotState.rowExpandedPaths.length) > 0 ||
        (pivotState === null || pivotState === void 0 ? void 0 : pivotState.columnExpandedPaths.length) > 0);
}
export function getCellValue(answerColumn, cellValue) {
    if (_.isNull(cellValue) &&
        isEffectivelyNumeric(answerColumn) &&
        isGrowth(answerColumn)) {
        return 0;
    }
    return cellValue;
}
export const getMeasureValuesColumns = (chartModel) => { var _a, _b; return ((_b = _.first((_a = chartModel === null || chartModel === void 0 ? void 0 : chartModel.config) === null || _a === void 0 ? void 0 : _a.axisConfig)) === null || _b === void 0 ? void 0 : _b.measureValues) || []; };
export const getMeasureValuesColumnsByName = (chartModel, name) => {
    const measureValuesColumns = getMeasureValuesColumns(chartModel);
    return measureValuesColumns.filter(column => getName(column) === name);
};
export const getCalculatedSummaryValueForMeasureValueColumn = (summaryCell, grandTotalCell, pivotSummaryMode, getSummaryCellValueFromModel, fetchPivotSummaryFromBackend) => {
    const summaryValue = fetchPivotSummaryFromBackend
        ? getSummaryCellValueFromModel(summaryCell)
        : summaryCell.value();
    const { value: cellValue, key } = getKeyValueFromCellValue(summaryValue);
    if (_.isNil(grandTotalCell)) {
        if (!_.isNil(key)) {
            return `${key}:1`;
        }
        return undefined;
    }
    if (grandTotalCell === summaryCell.grandTotal() &&
        pivotSummaryMode !== 'percentOfGrandTotal') {
        if (!_.isNil(key)) {
            return `${key}:1`;
        }
        return undefined;
    }
    const grandTotalCellValue = fetchPivotSummaryFromBackend
        ? getSummaryCellValueFromModel(grandTotalCell)
        : grandTotalCell.value();
    const isSummaryUndefined = _.isArray(summaryValue) || _.isNil(summaryValue);
    const isGrandTotalUndefined = _.isArray(grandTotalCellValue) || _.isNil(grandTotalCellValue);
    if (isSummaryUndefined || isGrandTotalUndefined) {
        return undefined;
    }
    const { value: gtValue } = getKeyValueFromCellValue(grandTotalCellValue);
    return `${key}:${_.isFinite(cellValue / gtValue) ? cellValue / gtValue : undefined}`;
};
//# sourceMappingURL=pivot-data-model-utils.js.map